import React from 'react';
import { createPortal } from 'react-dom';
import { SettingsIcon } from 'lucide-react';
import { FieldDefinition, HighlightRule, HighlightTone, LayoutSettings, LoanRecord, PaletteColor, PanelField, ToneColors } from '../../types/highlights';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { palette, paletteOrder, styleForTone, toneLabels, toneOrder } from '../../utils/palette';
import { columnWidthOptions, textScaleOrder, textScaleStyles } from '../../utils/layout';
import { describeRule, ruleMatches } from '../../utils/rules';
interface HighlightSettingsProps {
  fields: PanelField[];
  byId: Map<string, FieldDefinition>;
  loan: LoanRecord;
  toneColors: ToneColors;
  rules: HighlightRule[];
  layout: LayoutSettings;
  onSetToneColor: (tone: HighlightTone, color: PaletteColor) => void;
  onClearAllOverrides: () => void;
  onSetLayout: (patch: Partial<LayoutSettings>) => void;
  onNormalizeFieldSizes: () => void;
  onManageRules: (fieldId: string | null) => void;
  onResetSettings: () => void;
}
export function HighlightSettings({
  fields,
  byId,
  loan,
  toneColors,
  rules,
  layout,
  onSetToneColor,
  onClearAllOverrides,
  onSetLayout,
  onNormalizeFieldSizes,
  onManageRules,
  onResetSettings
}: HighlightSettingsProps) {
  const {
    open,
    setOpen,
    position,
    triggerRef,
    popoverRef,
    width,
    maxHeight
  } = useAnchoredPopover({
    align: 'right',
    width: 380,
    maxHeight: 520
  });
  const overrideCount = fields.filter(field => field.colorOverride !== undefined).length;
  const sizedCount = fields.filter(field => field.span !== undefined && field.span !== 'auto' || field.maxChars !== undefined).length;
  return <>
      <button ref={triggerRef} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(value => !value)} className={['flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', open ? 'border-[#0b5cab] bg-[#f3f8ff] text-[#0b5cab]' : 'border-[#cfe0f5] bg-white text-[#0b5cab] hover:bg-[#f3f8ff]'].join(' ')} data-id="element-63" data-mp="3j1v3i0t3oz2x">
        <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-64" data-mp="1o051cdbj7wd" />
        Configure
      </button>

      {open && position ? createPortal(<div ref={popoverRef} role="dialog" aria-label="Configure highlight colors and rules" style={{
      top: position.top,
      left: position.left,
      width,
      maxHeight
    }} className="fixed z-[9999] flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl" data-id="element-65" data-mp="bis46kmju2t9">
              <header className="flex items-center justify-between border-b border-slate-100 px-3 py-2" data-id="element-66" data-mp="uakug7a8h9xa">
                <h3 className="text-[13px] font-bold text-slate-900" data-id="element-67" data-mp="z95t429u7ljm">
                  Sizing, Colors &amp; Rules
                </h3>
                <button type="button" onClick={onResetSettings} className="rounded px-1.5 py-0.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800" data-id="element-68" data-mp="19e5gdifcc8n7">
                  Reset
                </button>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto" data-id="element-69" data-mp="vfo0u7zla2gd">
                <section className="border-b border-slate-100 px-3 py-2.5" data-id="element-70" data-mp="1s2iuaa0qkkti">
                  <p className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-71" data-mp="1n4hb7p3b8li1">
                    Sizing
                  </p>

                  <div className="flex items-start justify-between gap-3 pb-2.5" data-id="element-72" data-mp="1ibxw2noz5ib4">
                    <div className="min-w-0" data-id="element-73" data-mp="1ebxi145sf94l">
                      <p className="text-[12.5px] font-medium text-slate-800" data-id="element-74" data-mp="2au3bygzbz8s5">
                        Uniform sizing
                      </p>
                      <p className="text-[11px] leading-snug text-slate-500" data-id="element-75" data-mp="2622211pmxu5a">
                        Every chip gets one equal column. Per-field widths are ignored while
                        this is on.
                      </p>
                    </div>
                    <button type="button" role="switch" aria-checked={layout.uniformSizing} aria-label="Uniform sizing" onClick={() => onSetLayout({
              uniformSizing: !layout.uniformSizing
            })} className={['relative mt-0.5 h-4 w-8 shrink-0 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', layout.uniformSizing ? 'bg-[#0b5cab]' : 'bg-slate-300'].join(' ')} data-id="element-76" data-mp="1hlfati1kmtb9">
                      <span aria-hidden="true" className={['absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', layout.uniformSizing ? 'left-[18px]' : 'left-0.5'].join(' ')} data-id="element-77" data-mp="2s9walk6u5mgi" />
                    </button>
                  </div>

                  <p className="pb-1 text-[11px] font-medium text-slate-600" data-id="element-78" data-mp="vcrt8k186j8e">Column width</p>
                  <div role="radiogroup" aria-label="Column width" className="flex items-center gap-1 pb-2.5" data-id="element-79" data-mp="qd5c3zia0iub">
                    {columnWidthOptions.map(option => {
              const active = layout.columnWidth === option.value;
              return <button key={option.value} type="button" role="radio" aria-checked={active} onClick={() => onSetLayout({
                columnWidth: option.value
              })} className={['flex-1 rounded border px-2 py-1 text-[12px] font-medium transition', active ? 'border-[#0b5cab] bg-[#f3f8ff] text-[#0b5cab]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'].join(' ')} data-id="element-80" data-mp="cjntcgxeza5c">
                          {option.label}
                        </button>;
            })}
                  </div>

                  <p className="pb-1 text-[11px] font-medium text-slate-600" data-id="element-81" data-mp="1cdco6xb6t43s">Text size</p>
                  <div role="radiogroup" aria-label="Text size" className="flex items-center gap-1 pb-2.5" data-id="element-82" data-mp="1hd8skxnsqxjv">
                    {textScaleOrder.map(scale => {
              const active = layout.textScale === scale;
              return <button key={scale} type="button" role="radio" aria-checked={active} onClick={() => onSetLayout({
                textScale: scale
              })} className={['flex-1 rounded border px-2 py-1 text-[12px] font-medium transition', active ? 'border-[#0b5cab] bg-[#f3f8ff] text-[#0b5cab]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'].join(' ')} data-id="element-83" data-mp="tm3dwltlebb5">
                          {textScaleStyles[scale].label}
                        </button>;
            })}
                  </div>

                  <div className="flex items-center justify-between gap-3" data-id="element-84" data-mp="2a5ar58wzkv48">
                    <p className="text-[11px] leading-snug text-slate-500" data-id="element-85" data-mp="257sr680y5q8d">
                      {sizedCount === 0 ? 'No per-field width or character limits set' : `${sizedCount} field${sizedCount === 1 ? '' : 's'} with custom width or character limit`}
                    </p>
                    <button type="button" disabled={sizedCount === 0} onClick={onNormalizeFieldSizes} className="shrink-0 rounded border border-slate-200 px-2 py-1 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-86" data-mp="3t2nrgrk2klns">
                      Make uniform
                    </button>
                  </div>
                </section>

                <section className="border-b border-slate-100 px-3 py-2.5" data-id="element-87" data-mp="31du3u8rc8646">
                  <p className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-88" data-mp="31bq1hv937f60">
                    Status colors
                  </p>
                  <ul className="space-y-2" data-id="element-89" data-mp="2relc7mjy33kz">
                    {toneOrder.map(tone => <li key={tone} className="flex items-center justify-between gap-2" data-id="element-90" data-mp="27t33i4hfvlk8">
                        <span className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[11px] font-bold ${styleForTone(tone, toneColors).chip} ${styleForTone(tone, toneColors).text}`} data-id="element-91" data-mp="23t198ge19uez">
                          {toneLabels[tone]}
                        </span>
                        <div role="radiogroup" aria-label={`${toneLabels[tone]} color`} className="flex items-center gap-1" data-id="element-92" data-mp="2qr5weud3y2nb">
                          {paletteOrder.map(color => {
                  const active = toneColors[tone] === color;
                  return <button key={color} type="button" role="radio" aria-checked={active} aria-label={`${toneLabels[tone]}: ${palette[color].label}`} title={palette[color].label} onClick={() => onSetToneColor(tone, color)} className={['h-5 w-5 rounded border-2 transition', palette[color].swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-93" data-mp="3ah7tof9m8ksp" />;
                })}
                        </div>
                      </li>)}
                  </ul>
                </section>

                <section className="border-b border-slate-100 px-3 py-2.5" data-id="element-94" data-mp="1kgmkur5aregf">
                  <div className="flex items-center justify-between gap-3 pb-1.5" data-id="element-95" data-mp="3w4ht8x4odcql">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-96" data-mp="3w2oh52bam0b7">
                      Custom rules
                    </p>
                    <button type="button" onClick={() => {
              setOpen(false);
              onManageRules(null);
            }} className="rounded border border-[#cfe0f5] bg-white px-2 py-1 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff]" data-id="element-97" data-mp="8z07x72yekh2">
                      Manage rules
                    </button>
                  </div>
                  {rules.length === 0 ? <p className="py-2 text-[12px] text-slate-500" data-id="element-98" data-mp="wol2kn8gz666">
                      No rules yet. Add one to color a field from any loan value.
                    </p> : <ul className="divide-y divide-slate-100" data-id="element-99" data-mp="2yh0ydk7rfgi5">
                      {rules.map(rule => {
              const target = byId.get(rule.targetFieldId);
              const matching = rule.enabled && ruleMatches(rule, loan, byId);
              return <li key={rule.id} className="flex items-start gap-2 py-1.5" data-id="element-100" data-mp="eoa17qd266kt">
                            <span aria-hidden="true" className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-sm ${styleForTone(rule.tone, toneColors).swatch} ${rule.enabled ? '' : 'opacity-30'}`} data-id="element-101" data-mp="ajshtsam1vak" />
                            <div className="min-w-0 flex-1" data-id="element-102" data-mp="y9wj3t0btiye">
                              <p className="truncate text-[12.5px] font-medium text-slate-800" data-id="element-103" data-mp="tbuzn6yznhme">
                                {target?.label ?? 'Unknown field'}
                              </p>
                              <p className="truncate text-[11px] text-slate-500" data-id="element-104" data-mp="12cjgsajohy3i">
                                {describeRule(rule, byId)}
                              </p>
                            </div>
                            <span className={['mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold', !rule.enabled ? 'bg-slate-100 text-slate-500' : matching ? 'bg-[#0b5cab] text-white' : 'bg-slate-100 text-slate-500'].join(' ')} data-id="element-105" data-mp="1qx610pwkzpxr">
                              {!rule.enabled ? 'Off' : matching ? 'Matching' : 'Idle'}
                            </span>
                          </li>;
            })}
                    </ul>}
                </section>

                <section className="flex items-center justify-between gap-3 px-3 py-2.5" data-id="element-106" data-mp="p8kdkvwlyxu4">
                  <div data-id="element-107" data-mp="h73c8gjfwarh">
                    <p className="text-[12.5px] font-medium text-slate-800" data-id="element-108" data-mp="h7ml77r43otd">Manual colors</p>
                    <p className="text-[11px] text-slate-500" data-id="element-109" data-mp="c803al0c9ke8">
                      {overrideCount === 0 ? 'No fields manually colored' : `${overrideCount} field${overrideCount === 1 ? '' : 's'} overridden`}
                    </p>
                  </div>
                  <button type="button" disabled={overrideCount === 0} onClick={onClearAllOverrides} className="shrink-0 rounded border border-slate-200 px-2 py-1 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-110" data-mp="3umj22tqhaic2">
                    Clear all
                  </button>
                </section>
              </div>
            </div>, document.body) : null}
    </>;
}